import { z } from "zod";
// ─── Semantic Search ───────────────────────────────────────
export const SemanticSearchQuerySchema = z.object({
    query: z.string().min(1).max(1000),
    scope: z.enum(["contacts", "documents", "messages", "all"]).default("all"),
    limit: z.coerce.number().int().min(1).max(50).default(10),
});
export const SemanticSearchResultSchema = z.object({
    id: z.string(),
    type: z.enum(["contact", "company", "document", "message"]),
    title: z.string(),
    snippet: z.string(),
    score: z.number(),
    metadata: z.record(z.string(), z.unknown()).optional(),
});
export const SemanticSearchResponseSchema = z.object({
    results: z.array(SemanticSearchResultSchema),
    query: z.string(),
    totalResults: z.number().int(),
});
// ─── Natural Language Query ────────────────────────────────
export const NaturalLanguageQuerySchema = z.object({
    question: z.string().min(1).max(2000),
});
export const NaturalLanguageResponseSchema = z.object({
    answer: z.string(),
    query: z.string(),
    sources: z
        .array(z.object({
        type: z.string(),
        id: z.string(),
        title: z.string(),
    }))
        .optional(),
});
// ─── Help Assistant ────────────────────────────────────────
export const HelpQuerySchema = z.object({
    question: z.string().min(1).max(2000),
    currentRoute: z.string().max(200).optional(),
    context: z.record(z.string(), z.unknown()).optional(),
});
export const HelpResponseSchema = z.object({
    answer: z.string(),
    relatedArticles: z
        .array(z.object({
        title: z.string(),
        path: z.string(),
    }))
        .optional(),
});
// ─── Summarisation ─────────────────────────────────────────
export const SummariseRequestSchema = z.object({
    type: z.enum(["document", "thread", "meeting_notes"]),
    sourceId: z.string().uuid(),
});
export const SummariseResponseSchema = z.object({
    summary: z.string(),
    sourceId: z.string().uuid(),
    generatedAt: z.string().datetime(),
});
// ─── Insights AI (product assistant) ───────────────────────
export const AiSkillIdSchema = z.enum(["artefact-discipline", "fact-ledger", "personal-sidecar"]);
export const AiSkillSchema = z.object({
    id: AiSkillIdSchema,
    name: z.string().min(1).max(120),
    description: z.string().max(500),
    alwaysOn: z.boolean(),
    version: z.string().min(1).max(40),
});
export const AiSkillListResponseSchema = z.object({
    skills: z.array(AiSkillSchema),
});
export const AiSourceTypeSchema = z.enum([
    "note",
    "document",
    "project",
    "meeting",
    "deal",
    "ideation_map",
    "working_document",
    "sharepoint_file",
    "outlook_message",
    "subject",
]);
const TenantAiSourceRefSchema = z.object({
    type: z.enum([
        "note",
        "document",
        "project",
        "meeting",
        "deal",
        "ideation_map",
        "working_document",
        "subject",
    ]),
    id: z.string().uuid(),
});
const GraphAiSourceRefSchema = z.object({
    type: z.enum(["sharepoint_file", "outlook_message"]),
    /** Opaque Graph composite (`sp:…` / `ol:…`), not a tenant UUID. */
    id: z.string().min(1).max(512),
});
export const AiSourceRefSchema = z.discriminatedUnion("type", [
    TenantAiSourceRefSchema,
    GraphAiSourceRefSchema,
]);
export const AiConversationSchema = z.object({
    id: z.string().uuid(),
    title: z.string(),
    provider: z.string(),
    model: z.string(),
    sourceScope: z.array(AiSourceRefSchema),
    projectId: z.string().uuid().nullable().optional(),
    ragEnabled: z.boolean().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    messageCount: z.number().int().nonnegative(),
});
export const AiConversationListResponseSchema = z.object({
    conversations: z.array(AiConversationSchema),
});
export const CreateAiConversationSchema = z.object({
    title: z.string().max(300).optional(),
    sources: z.array(AiSourceRefSchema).max(20).default([]),
    ragEnabled: z.boolean().optional(),
    projectId: z.string().uuid().optional(),
});
export const AiMessageRoleSchema = z.enum(["user", "assistant", "system"]);
export const AiCitationSchema = z.object({
    id: z.string().uuid().optional(),
    sourceType: z.string(),
    sourceId: z.string(),
    title: z.string(),
    excerpt: z.string().max(2000).optional(),
});
export const AiMessageSchema = z.object({
    id: z.string().uuid(),
    conversationId: z.string().uuid(),
    role: AiMessageRoleSchema,
    content: z.string(),
    inputTokens: z.number().int().nonnegative().nullable(),
    outputTokens: z.number().int().nonnegative().nullable(),
    latencyMs: z.number().int().nonnegative().nullable(),
    error: z.string().nullable(),
    citations: z.array(AiCitationSchema).default([]),
    createdAt: z.string().datetime(),
});
export const AiConversationDetailSchema = z.object({
    conversation: AiConversationSchema,
    messages: z.array(AiMessageSchema),
});
export const AiAttachmentRefSchema = z.object({
    key: z.string().min(1).max(500),
    filename: z.string().min(1).max(300),
    contentType: z.string().min(1).max(120),
    size: z
        .number()
        .int()
        .positive()
        .max(25 * 1024 * 1024),
});
export const PostAiMessageSchema = z.object({
    content: z.string().min(1).max(20_000),
    sources: z.array(AiSourceRefSchema).max(20).optional(),
    ragEnabled: z.boolean().optional(),
    /** Conversation-scoped uploads (signed PUT first). */
    attachments: z.array(AiAttachmentRefSchema).max(10).optional(),
    /** Opt-in skills (e.g. personal-sidecar). Always-on skills are forced server-side. */
    skillIds: z.array(AiSkillIdSchema).max(10).optional(),
});
export const AiAttachmentUploadRequestSchema = z.object({
    conversationId: z.string().uuid(),
    filename: z.string().min(1).max(300),
    contentType: z.string().min(1).max(120),
    size: z
        .number()
        .int()
        .positive()
        .max(25 * 1024 * 1024),
});
export const AiAttachmentUploadResponseSchema = z.object({
    key: z.string(),
    uploadUrl: z.string().url(),
    expiresAt: z.string().datetime(),
    /** Resolved MIME used when signing the PUT (may differ from the browser's octet-stream). */
    contentType: z.string().min(1).max(120),
});
export const AiArtifactKindSchema = z.enum([
    "note",
    "brief",
    "memo",
    "faq",
    "checklist",
    "interactive",
    "other",
]);
const BODY_JSX_MAX = 512_000;
function refineInteractiveBodyJsx(data, ctx) {
    const jsx = data.bodyJsx;
    if (data.kind === "interactive") {
        if (jsx == null || jsx.trim() === "") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "bodyJsx is required when kind is interactive",
                path: ["bodyJsx"],
            });
        }
        return;
    }
    if (jsx != null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "bodyJsx is only allowed when kind is interactive",
            path: ["bodyJsx"],
        });
    }
}
export const CreateAiArtifactSchema = z
    .object({
    conversationId: z.string().uuid(),
    messageId: z.string().uuid().optional(),
    kind: AiArtifactKindSchema.default("note"),
    title: z.string().min(1).max(300),
    bodyMd: z.string().min(1).max(200_000),
    bodyJsx: z.string().max(BODY_JSX_MAX).nullable().optional(),
    linkedEntityType: z.string().max(40).optional(),
    linkedEntityId: z.string().uuid().optional(),
})
    .superRefine(refineInteractiveBodyJsx);
export const AiArtifactSchema = z
    .object({
    id: z.string().uuid(),
    conversationId: z.string().uuid(),
    messageId: z.string().uuid().nullable(),
    kind: AiArtifactKindSchema,
    title: z.string(),
    bodyMd: z.string(),
    bodyJsx: z.string().max(BODY_JSX_MAX).nullable(),
    linkedEntityType: z.string().nullable(),
    linkedEntityId: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
})
    .superRefine(refineInteractiveBodyJsx);
export const AiUsageMeResponseSchema = z.object({
    requestCount: z.number().int().nonnegative(),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
});
export const AiUsageTenantResponseSchema = z.object({
    requestCount: z.number().int().nonnegative(),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    uniqueUsers: z.number().int().nonnegative(),
});
/** Tenant Insights AI monthly token hard-caps (null = unlimited). */
export const AiBudgetSettingsSchema = z.object({
    tenantMonthlyTokenCap: z.number().int().positive().nullable(),
    defaultUserMonthlyTokenCap: z.number().int().positive().nullable(),
    updatedAt: z.string().datetime().nullable(),
    updatedBy: z.string().nullable(),
});
export const AiBudgetSettingsUpdateSchema = z.object({
    tenantMonthlyTokenCap: z.number().int().positive().nullable(),
    defaultUserMonthlyTokenCap: z.number().int().positive().nullable(),
});
export const AiUserBudgetSchema = z.object({
    userEmail: z.string().email().max(320),
    monthlyTokenCap: z.number().int().positive().nullable(),
});
export const AiUserBudgetListResponseSchema = z.object({
    budgets: z.array(z.object({
        userEmail: z.string(),
        monthlyTokenCap: z.number().int().positive(),
        updatedAt: z.string().datetime(),
    })),
});
export const AiSourceSearchHitSchema = z.object({
    type: AiSourceTypeSchema,
    id: z.string().min(1).max(512),
    title: z.string(),
    subtitle: z.string().optional(),
});
export const AiSourceSearchResponseSchema = z.object({
    hits: z.array(AiSourceSearchHitSchema),
});
export const AiArtifactListResponseSchema = z.object({
    artifacts: z.array(AiArtifactSchema),
});
const ConfirmableAiToolIdSchema = z.enum([
    "save_artefact",
    "create_note",
    "create_task",
    "create_board_memo",
    "attach_to_deal",
    "draft_tracked_email",
    "capture_to_ideation",
    "propose_subject_fact",
    "propose_derived_claim",
    "record_decision",
    "web_search",
    "revise_working_document",
    "share_to_portal",
    "share_portal_document",
    "revoke_portal_document",
    "publish_portal_object",
    "revoke_portal_object",
    "publish_portal_brief",
    "provision_portal",
    "create_data_room",
    "create_proforma_entity",
    "record_entity_formation",
    "admit_data_room_evidence",
    "set_data_room_evidence_state",
]);
export const ConfirmAiToolSchema = z.union([
    z.object({
        confirmationToken: z.string().min(80).max(20_000),
        messageId: z.string().uuid().optional(),
    }),
    // Compatibility for older, already-rendered conversations. New pending
    // actions are always issued as signed confirmation envelopes.
    z.object({
        conversationId: z.string().uuid(),
        toolId: ConfirmableAiToolIdSchema,
        args: z.record(z.string(), z.unknown()),
        messageId: z.string().uuid().optional(),
    }),
]);
export const ConfirmAiToolResponseSchema = z.object({
    decision: z.enum(["allow", "deny", "pending_confirm"]),
    summary: z.string().optional(),
    entityType: z.string().optional(),
    entityId: z.string().uuid().optional(),
});
export const CancelAiToolSchema = z.object({
    confirmationToken: z.string().min(80).max(20_000),
});
//# sourceMappingURL=ai.js.map